import { Staff, Student } from '@repo/types/domain';
import { StaffUserGroups, UserGroup } from '@repo/validation';
import { User, UserWithGroups } from './types';

type UserInput = User | UserGroup[] | UserWithGroups;

/**
 * Discriminates the `Student | Staff` union by the id each record carries. This asks which
 * record was read, not what its groups grant — `isStudent` and `isStaff` answer that.
 */
export const isStudentRecord = (user: object): user is Student => 'studentId' in user;

export const isStaffRecord = (user: object): user is Staff => 'staffId' in user;

const isArray = (input: unknown): input is UserGroup[] =>
  typeof input === 'object' && Array.isArray(input);

const hasGroups = (input: unknown): input is UserWithGroups =>
  typeof input === 'object' && input !== null && 'groups' in input;

/**
 * Checks if the user is an admin.
 * @param user The user to check.
 * @returns True if the user is an admin, false otherwise.
 */
export const isAdmin = (user: UserInput) =>
  (hasGroups(user) && user.groups.includes(UserGroup.Admins)) ||
  (isArray(user) && user.includes(UserGroup.Admins));

/**
 * Checks if the user is a manager.
 * @param user The user to check.
 * @returns True if the user is a manager, false otherwise.
 */
export const isManager = (user: UserInput) =>
  (hasGroups(user) && user.groups.includes(UserGroup.Managers)) ||
  (isArray(user) && user.includes(UserGroup.Managers));

/**
 * Checks if the user is an owner.
 * @param user The user to check.
 * @returns True if the user is an owner, false otherwise.
 */
export const isOwner = (user: UserInput) =>
  (hasGroups(user) && user.groups.includes(UserGroup.Owners)) ||
  (isArray(user) && user.includes(UserGroup.Owners));

/**
 * Checks if the user is a teacher.
 * @param user The user to check.
 * @returns True if the user is a teacher, false otherwise.
 */
export const isTeacher = (user: UserInput) =>
  (hasGroups(user) && user.groups.includes(UserGroup.Teachers)) ||
  (isArray(user) && user.includes(UserGroup.Teachers));

/**
 * Checks if the user is a student.
 * @param user The user to check.
 * @returns True if the user is a student, false otherwise.
 */
export const isStudent = (user: UserInput) =>
  isStudentRecord(user) ||
  (hasGroups(user) && user.groups.includes(UserGroup.Students)) ||
  (isArray(user) && user.includes(UserGroup.Students));

/**
 * Checks if the user is a staff member (admin, manager, owner, or teacher).
 * @param user The user to check.
 * @returns True if the user is a staff member, false otherwise.
 */
export const isStaff = (user: UserInput) =>
  (hasGroups(user) && user.groups.some(group => StaffUserGroups.includes(group as UserGroup))) ||
  (isArray(user) && user.some(group => StaffUserGroups.includes(group)));

// Every entry has to be recognised. The checks above answer only from the groups they know,
// so one entry they do not know is a privilege that cannot be bounded — a renamed `Owners`
// reads as a plain teacher.
const isUserGroupArray = (groups: unknown): groups is UserGroup[] =>
  Array.isArray(groups) &&
  groups.length > 0 &&
  groups.every(group => Object.values(UserGroup).includes(group as UserGroup));

/**
 * Checks whether the user's role can be read at all. A stored record can reach a guard
 * with its groups missing or unreadable, and every check above answers `false` for one —
 * which a guard written as "deny when the target is privileged" reads as "not
 * privileged". Ask this first so an unreadable role denies rather than allows.
 * @param user The user to check.
 * @returns True if the user's role can be determined, false otherwise.
 */
export const hasResolvedRole = (user: UserInput) => {
  if (isArray(user)) {
    return isUserGroupArray(user);
  }
  if (isStudentRecord(user)) {
    return true;
  }
  return hasGroups(user) && isUserGroupArray(user.groups);
};

/**
 * The groups a user is in. A student record carries none of its own, being a student by
 * definition.
 * @param user The user to read.
 * @returns The user's groups, which may hold values outside `UserGroup` for a stored record.
 */
export const getUserGroups = (user: UserInput): unknown[] => {
  if (isArray(user)) {
    return user;
  }
  if (isStudentRecord(user)) {
    return [UserGroup.Students];
  }
  return hasGroups(user) && Array.isArray(user.groups) ? user.groups : [];
};
